// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Digital signatures, and the single most misreported fact in PDF forensics.
 *
 * A signature's /ByteRange is two spans: [a b c d] means "bytes a..a+b and
 * c..c+d are covered by this signature". The hole between them is the
 * signature's own /Contents value — the certificate and hash could not
 * plausibly sign themselves — and it is NOT concealed content. This file never
 * calls it a gap, an unsigned region, or anything else a reader would hear as
 * "something is hidden in there", because that reading is always false.
 *
 * The other half is bytes AFTER the last covered span. Those exist constantly,
 * for reasons that are not merely innocent but are how the format is designed
 * to work:
 *
 *   - a second signature. With N signatures, the first N-1 necessarily have
 *     bytes after them. That is what countersigning IS.
 *   - long-term validation data. ISO 32000-2 explicitly carves out an
 *     incremental update that adds only a document security store or a
 *     document timestamp; Adobe Reader adds these by itself when it opens a
 *     signed file, without being asked.
 *   - permitted changes. A certifying signature's DocMDP /P says what may
 *     follow it: 2 permits filling in forms and signing, 3 additionally
 *     permits adding, changing and deleting annotations.
 *
 * So the number is reported, the reason for it is looked up in the revisions
 * that actually follow, and no verdict is offered. What peelback can honestly
 * say is "N bytes are outside this signature's covered range, and the saves in
 * that range did X". Whether X was allowed is a question about a certificate
 * chain this tool deliberately does not have.
 *
 * ISO 32000-1 §12.8; ISO 32000-2 §12.8.5 (DSS); ETSI EN 319 142 (PAdES).
 */

import { PdfDocument, decodeTextString } from './doc.ts';
import type { Revision } from './revisions.ts';
import { PdfName, type PdfDict, type PdfValue } from './lexer.ts';

export interface SignatureFinding {
  fieldName: string;
  signerName: string | null;
  signedAt: string | null;
  reason: string | null;
  location: string | null;
  /** /SubFilter — the signature format, e.g. adbe.pkcs7.detached. */
  subFilter: string | null;
  filter: string | null;
  /**
   * The /ByteRange, but ONLY when it passes `isSaneByteRange`. Null means
   * either that there is none or that the one written could not be trusted —
   * and in that case nothing is said about what the signature covers.
   */
  byteRange: number[] | null;
  /** What the file actually wrote, whether or not it made sense. */
  declaredByteRange: number[] | null;
  /** True when the first covered span starts at byte 0, as it must. */
  startsAtZero: boolean;
  /** Size of the signature value sitting between the two covered spans. */
  signatureValueBytes: number;
  /** One past the last byte this signature covers. */
  signedEnd: number;
  /** Bytes in the file beyond that point. */
  bytesAfter: number;
  /** Position in the file's signing order, 1 = signed first. */
  order: number;
  /** How many signatures there are in total. */
  total: number;
  /**
   * DocMDP /P, when this is a certifying signature: 1 permits no later
   * changes, 2 permits form fill-in and signing, 3 additionally permits
   * annotations. Null when this is an ordinary approval signature, which
   * places no restriction on later saves at all.
   */
  docMdpLevel: number | null;
  /** What the saves after this signature actually did. */
  laterSaves: LaterSave[];
  /** Non-judgmental sentence describing the bytes after this signature. */
  afterDescription: string;
}

export interface LaterSave {
  index: number;
  bytes: number;
  changes: string[];
}

function numbersFrom(value: PdfValue | undefined): number[] | null {
  if (!Array.isArray(value)) return null;
  if (!value.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  return (value as number[]).map((v) => Math.trunc(v));
}

/**
 * A /ByteRange must be sane before anything is said about it.
 *
 * Without this, a document can be written whose /ByteRange makes the tool
 * announce that bytes were appended after signing when they were not — which
 * is the single most accusatory sentence peelback can produce, so it is
 * exactly the one somebody would want to induce. The five conditions are
 * pdf.js's own production predicate: exactly four non-negative integers, the
 * first span starting at byte 0 and non-empty, the two spans in order and
 * non-overlapping, and the whole thing inside the file.
 */
export function isSaneByteRange(range: number[] | null, fileLength: number): boolean {
  if (!range || range.length !== 4 || fileLength <= 0) return false;
  const [a, b, c, d] = range;
  if (![a, b, c, d].every((n) => Number.isInteger(n) && n >= 0)) return false;
  return a === 0 && b > 0 && a + b <= c && c + d <= fileLength;
}

/** /Reference → /TransformMethod /DocMDP → /TransformParams → /P. */
function docMdpLevel(doc: PdfDocument, sig: PdfDict): number | null {
  const refs = doc.array(sig, 'Reference');
  if (!refs) return null;
  for (const ref of refs) {
    const d = doc.dict(ref);
    if (!d) continue;
    const method = doc.name(d, 'TransformMethod');
    if (method !== 'DocMDP') continue;
    const params = doc.dict(d.get('TransformParams'));
    const p = doc.number(params, 'P');
    if (p !== null) return p;
  }
  return null;
}

const CHANGE_WORDS: Record<string, string> = {
  signature: 'added a signature',
  'validation-data': 'added validation data for a signature',
  annotation: 'added or changed a comment or annotation',
  'form-fill': 'filled in a form field',
  'page-content': 'changed what is on a page',
  structure: 'changed the document’s structure or its information fields',
  unclassified: 'changed something this tool could not classify',
};

export function describeChange(change: string): string {
  return CHANGE_WORDS[change] ?? CHANGE_WORDS.unclassified;
}

export function readSignatures(
  doc: PdfDocument,
  fileLength: number,
  revisions: Revision[],
): SignatureFinding[] {
  const fields = doc.signatureFields();
  if (fields.length === 0) return [];

  const partial = fields.map(({ fieldName, sig }) => {
    const declared = numbersFrom(doc.get(sig, 'ByteRange'));
    const sane = isSaneByteRange(declared, fileLength);
    const byteRange = sane ? declared : null;
    const name = doc.string(sig, 'Name');
    const m = doc.string(sig, 'M');
    const reason = doc.string(sig, 'Reason');
    const location = doc.string(sig, 'Location');
    const subFilterName = doc.get(sig, 'SubFilter');
    const filterName = doc.get(sig, 'Filter');

    let signedEnd = 0;
    let signatureValueBytes = 0;
    let startsAtZero = false;
    if (byteRange) {
      startsAtZero = byteRange[0] === 0;
      signedEnd = byteRange[2] + byteRange[3];
      // The hole between the first span's end and the second span's start.
      signatureValueBytes = Math.max(0, byteRange[2] - (byteRange[0] + byteRange[1]));
    }

    return {
      fieldName,
      declaredByteRange: declared,
      signerName: name ? decodeTextString(name) : null,
      signedAt: m ? decodeTextString(m) : null,
      reason: reason ? decodeTextString(reason) : null,
      location: location ? decodeTextString(location) : null,
      subFilter: subFilterName instanceof PdfName ? subFilterName.name : null,
      filter: filterName instanceof PdfName ? filterName.name : null,
      byteRange,
      startsAtZero,
      signatureValueBytes,
      signedEnd,
      bytesAfter: Math.max(0, fileLength - signedEnd),
      docMdpLevel: docMdpLevel(doc, sig),
    };
  });

  // Signing order is byte order: a signature applied later necessarily covers
  // more of the file.
  partial.sort((a, b) => a.signedEnd - b.signedEnd);

  return partial.map((sig, i) => {
    const laterSaves: LaterSave[] = revisions
      .filter((r) => r.endOffset > sig.signedEnd)
      .map((r) => ({
        index: r.index,
        bytes: r.endOffset - Math.max(sig.signedEnd, r.index > 1 ? revisions[r.index - 2].endOffset : 0),
        changes: r.changes.map(describeChange),
      }));

    return {
      ...sig,
      order: i + 1,
      total: partial.length,
      laterSaves,
      afterDescription: describeAfter(sig, i + 1, partial.length, laterSaves),
    };
  });
}

function describeAfter(
  sig: {
    bytesAfter: number;
    byteRange: number[] | null;
    declaredByteRange: number[] | null;
    docMdpLevel: number | null;
  },
  order: number,
  total: number,
  laterSaves: LaterSave[],
): string {
  if (!sig.byteRange) {
    return sig.declaredByteRange
      ? 'The covered range this signature declares does not make sense as a range within this file — it does not start at ' +
          'byte 0, or its two spans overlap, or it reaches past the end. peelback will not say anything about what this ' +
          'signature covers, because a range that cannot be trusted cannot support a claim about appended bytes.'
      : 'This signature declares no covered range at all, so there is nothing to compare against the file. Nothing can be said about what it covers.';
  }
  if (sig.bytesAfter === 0) {
    return 'Everything in this file up to its end is inside this signature’s covered range.';
  }

  const n = sig.bytesAfter.toLocaleString();
  if (order < total) {
    return (
      `${n} bytes come after this signature’s covered range. This document has ${total} signatures, ` +
      `and this is the ${ordinal(order)}. Every signature except the last one necessarily has bytes after it — ` +
      'that is what countersigning is.'
    );
  }

  const actions = new Set(laterSaves.flatMap((s) => s.changes));
  const onlyValidation =
    actions.size > 0 && [...actions].every((a) => a.includes('validation data'));

  if (onlyValidation) {
    return (
      `${n} bytes come after the last signature’s covered range, and the saves in them added only validation data. ` +
      'A reader adds that by itself when it opens a signed file, so it is expected rather than notable.'
    );
  }

  const list = [...actions];
  const what = list.length > 0 ? ` The saves in them ${joinList(list)}.` : '';
  const mdp =
    sig.docMdpLevel === null
      ? ' This is an approval signature, which places no restriction on later saves.'
      : sig.docMdpLevel === 1
        ? ' This signature certifies the document with DocMDP level 1, which is the level that permits no later changes.'
        : sig.docMdpLevel === 2
          ? ' This signature certifies the document with DocMDP level 2, which permits later form filling and signing.'
          : ' This signature certifies the document with DocMDP level 3, which permits later form filling, signing and annotations.';

  return `${n} bytes come after the last signature’s covered range.${what}${mdp}`;
}

function ordinal(n: number): string {
  const suffix = ['th', 'st', 'nd', 'rd'][n % 100 >= 11 && n % 100 <= 13 ? 0 : Math.min(n % 10, 4)] ?? 'th';
  return `${n}${suffix}`;
}

function joinList(items: string[]): string {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
